import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { PlenaLogo } from "../components/PlenaLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AdminUsers } from "../components/admin/AdminUsers";
import { AdminTeams } from "../components/admin/AdminTeams";
import { InviteResultDialog } from "../components/admin/InviteResultDialog";
import { Plus, Building2, FolderKanban, CheckCircle2, ListTodo, LogOut, Trash2, ArrowUpRight, Upload, Moon, Sun, Users, UserRound, Send } from "lucide-react";

const inputCls = "mt-1.5 w-full h-10 px-3.5 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all";

const TABS = [
  { id: "customers", label: "Müşteriler", icon: Building2 },
  { id: "users", label: "Kullanıcılar", icon: UserRound },
  { id: "teams", label: "Gruplar", icon: Users },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [overview, setOverview] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", user_email: "", user_password: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = () => {
    api.get("/admin/customers").then((r) => setCustomers(r.data));
    api.get("/admin/overview").then((r) => setOverview(r.data));
    api.get("/admin/teams").then((r) => setTeams(r.data));
  };
  useEffect(load, []);

  const createCustomer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/customers", { ...form, user_password: form.user_password || null });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/admin/customers/${data.id}/logo`, fd);
      }
      toast.success("Müşteri oluşturuldu");
      setOpen(false);
      setForm({ name: "", user_email: "", user_password: "" });
      setLogoFile(null);
      load();
      if (Object.prototype.hasOwnProperty.call(data, "email_sent")) setInviteResult({ ...data, email: form.user_email });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeCustomer = async (c) => {
    if (!window.confirm(`${c.name} ve tüm projeleri silinecek. Emin misiniz?`)) return;
    await api.delete(`/admin/customers/${c.id}`);
    toast.success("Müşteri silindi");
    load();
  };

  const stats = [
    { label: "Müşteri", value: overview?.customers, icon: Building2, color: "#007AFF" },
    { label: "Proje", value: overview?.projects, icon: FolderKanban, color: "#AF52DE" },
    { label: "Task", value: overview?.tasks, icon: ListTodo, color: "#FF9500" },
    { label: "Tamamlanan", value: overview?.done_tasks, icon: CheckCircle2, color: "#34C759" },
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#161618]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 dark:bg-[#1C1C1E]/80 border-b border-slate-200/70 dark:border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <PlenaLogo />
            <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-white/10 rounded-full px-2.5 py-1">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <button data-testid="theme-toggle-button" onClick={toggle}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors">
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button data-testid="header-logout-button" onClick={logout}
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {!user?.mfa_enabled && <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-4 flex items-center justify-between gap-4">
          <p className="text-sm text-amber-900 dark:text-amber-200">Admin hesabınızda MFA etkin değil. Yönetim işlemlerine devam etmek için kurulumu tamamlayın.</p>
          <button onClick={() => navigate("/admin/mfa-setup")} className="shrink-0 h-9 px-4 rounded-lg bg-amber-600 text-white text-sm font-semibold">MFA kur</button>
        </div>}
        <div data-testid="admin-tabs" className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-200/60 dark:bg-white/10 border border-slate-300/40 dark:border-white/10 mb-8">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`h-9 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${tab === t.id ? "bg-white shadow-sm text-slate-900 dark:bg-[#3A3A3C] dark:text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "users" && <AdminUsers customers={customers} teams={teams} currentUserId={user?.id} onChanged={load} />}
        {tab === "teams" && <AdminTeams customers={customers} teams={teams} onChanged={load} />}
        {tab === "customers" && (<>
        <div className="flex items-end justify-between mb-8 animate-fade-up">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Müşteriler</h1>
            <p className="text-sm text-slate-500 mt-1.5">Tüm müşteri hesaplarını ve projelerini yönetin</p>
          </div>
          <button data-testid="admin-add-customer-button" onClick={() => setOpen(true)}
            className="h-10 px-4 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] active:scale-[0.98] text-white text-sm font-semibold transition-all shadow-lg shadow-[#0066CC]/25 flex items-center gap-2">
            <Plus size={16} /> Yeni Müşteri
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {stats.map((s, i) => (
            <div key={s.label} className="bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}15`, color: s.color }}>
                <s.icon size={17} />
              </div>
              <p className="text-2xl font-semibold font-display tracking-tight text-slate-900 dark:text-white">{s.value ?? "—"}</p>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        <div data-testid="admin-customer-list-container" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {customers.map((c, i) => (
            <div key={c.id} data-testid="admin-customer-card-item"
              onClick={() => navigate(`/workspace/${c.id}`)}
              className="group cursor-pointer bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_-6px_rgba(0,0,0,0.1)] hover:-translate-y-0.5 transition-all animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  {c.logo_path ? (
                    <img src={`${API}/files/${c.logo_path}`} alt={c.name} className="w-12 h-12 rounded-xl object-contain bg-slate-50 border border-slate-200/60 p-1.5" crossOrigin="use-credentials" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-white font-display font-semibold text-lg">
                      {c.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-display font-semibold text-slate-900 dark:text-white tracking-tight">{c.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{c.project_count} proje · {c.users?.length || 0} kullanıcı</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid="admin-customer-delete-button" onClick={(e) => { e.stopPropagation(); removeCustomer(c); }}
                    className="w-8 h-8 rounded-lg hover:bg-[#FF2D55]/10 text-slate-400 hover:text-[#FF2D55] flex items-center justify-center transition-colors">
                    <Trash2 size={14} />
                  </button>
                  <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center">
                    <ArrowUpRight size={14} />
                  </div>
                </div>
              </div>
              {c.users?.[0] && <p className="text-xs text-slate-400 mt-4">{c.users[0].email}</p>}
            </div>
          ))}
          {customers.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400 text-sm">Henüz müşteri yok. İlk müşterinizi oluşturun.</div>
          )}
        </div>
        </>)}
      </main>

      <InviteResultDialog result={inviteResult} onClose={() => setInviteResult(null)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Yeni Müşteri</DialogTitle>
          </DialogHeader>
          <form onSubmit={createCustomer} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Firma Adı</label>
              <input data-testid="customer-name-input" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ACME Corp" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Kullanıcı E-posta</label>
              <input data-testid="customer-email-input" required type="email" value={form.user_email}
                onChange={(e) => setForm({ ...form, user_email: e.target.value })} placeholder="musteri@firma.com" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Şifre <span className="normal-case tracking-normal text-slate-400">(boş bırakılırsa davet e-postası gönderilir)</span></label>
              <input data-testid="customer-password-input" type="password" minLength={8} value={form.user_password}
                onChange={(e) => setForm({ ...form, user_password: e.target.value })} placeholder="Opsiyonel · en az 8 karakter, harf + rakam" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Logo (opsiyonel)</label>
              <label className="mt-1.5 flex items-center gap-2 h-10 px-3.5 rounded-xl bg-slate-100/80 border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors">
                <Upload size={14} /> {logoFile ? logoFile.name : "Logo dosyası seçin"}
                <input data-testid="customer-logo-input" type="file" accept="image/*" className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <button data-testid="customer-create-submit-button" type="submit" disabled={saving}
              className="w-full h-10 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] text-white text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2">
              {form.user_password ? <Plus size={14} /> : <Send size={14} />}
              {saving ? "Oluşturuluyor..." : form.user_password ? "Müşteri Oluştur" : "Oluştur ve Davet Gönder"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
